// Line type configuration - single source of truth for colors, labels, and shortcuts
//
// Kept identical to the mobile palette (mobile/components/measurements/
// measureConstants.ts) so the same roof reads the same way on a phone, on a
// desktop and on a printed report. A rep who uses both surfaces should never
// have to translate between two colour languages.
//
// `dashPattern` is a deliberate second encoding, in pixels-on/pixels-off.
// Colour alone is fragile for this audience: roofers work outdoors in direct
// sun, often through polarised sunglasses, and red-ridge / green-eave is the
// most common colour-blind confusion there is.
export const LINE_TYPE_CONFIG = {
  ridge: { color: '#EF4444', label: 'Ridge', shortcut: 'R', dashPattern: null },
  hip: { color: '#F59E0B', label: 'Hip', shortcut: 'H', dashPattern: [6, 4] },
  valley: { color: '#0D9488', label: 'Valley', shortcut: 'V', dashPattern: [10, 4, 2, 4] },
  eave: { color: '#10B981', label: 'Eave', shortcut: 'E', dashPattern: null },
  rake: { color: '#7C3AED', label: 'Rake', shortcut: 'K', dashPattern: [12, 6] },
  flashing: { color: '#EC4899', label: 'Flashing', shortcut: 'F', dashPattern: [4, 4] },
} as const;

export type LineType = keyof typeof LINE_TYPE_CONFIG;

export const LINE_TYPES = Object.keys(LINE_TYPE_CONFIG) as LineType[];

export const SHORTCUT_TO_LINE_TYPE: Record<string, LineType> = Object.fromEntries(
  Object.entries(LINE_TYPE_CONFIG).map(([type, config]) => [config.shortcut.toLowerCase(), type as LineType])
) as Record<string, LineType>;

// Default polyline rendering options
export const LINE_STROKE_WEIGHT = 3;
export const LINE_STROKE_WEIGHT_HOVER = 6;
export const LINE_STROKE_OPACITY = 0.9;
export const SNAP_THRESHOLD_PX = 15;

// Pitch multiplier table: pitch ratio → slope factor multiplier
// True Area = Flat Area × multiplier
export const PITCH_MULTIPLIERS: Record<string, number> = {
  '1/12': 1.003, '2/12': 1.014, '3/12': 1.031, '4/12': 1.054,
  '5/12': 1.083, '6/12': 1.118, '7/12': 1.158, '8/12': 1.202,
  '9/12': 1.250, '10/12': 1.302, '11/12': 1.357, '12/12': 1.414,
  '13/12': 1.474, '14/12': 1.537, '15/12': 1.601, '16/12': 1.667,
  '17/12': 1.734, '18/12': 1.803,
};

export const PITCH_OPTIONS = Object.keys(PITCH_MULTIPLIERS);
export const DEFAULT_PITCH = '4/12';
export const DEFAULT_WASTE_FACTOR = 15;

/** Rise in twelfths, e.g. '6/12' → 6. */
export function pitchRise(pitch: string): number {
  const rise = parseFloat(pitch.split('/')[0]);
  return Number.isFinite(rise) ? rise : 6;
}

/** Slope factor for anything running straight up the slope (rake, area). */
export function slopeFactor(pitch: string): number {
  const table = PITCH_MULTIPLIERS[pitch];
  if (table) return table;
  return Math.sqrt(1 + (pitchRise(pitch) / 12) ** 2);
}

/**
 * Slope factor for hips and valleys, which travel diagonally rather than
 * straight up: 12√2 of horizontal run per 12 of common run. Noticeably
 * smaller than the rake factor — applying the rake factor to a hip, a common
 * shortcut, over-orders hip cap by roughly 5% at 6/12.
 */
export function diagonalSlopeFactor(pitch: string): number {
  return Math.sqrt(1 + (pitchRise(pitch) / (12 * Math.SQRT2)) ** 2);
}

/**
 * Convert a length measured on flat aerial imagery into the true length that
 * gets ordered.
 *
 * Eave and ridge are horizontal and are never corrected. Rake climbs the
 * slope. Hip and valley climb it diagonally. Treating a rake as uncorrected
 * under-orders that trim by about 12% at 6/12, which is why this is applied
 * at save time rather than left to whoever reads the number later.
 *
 * Mirrors mobile/components/measurements/roofPitch.ts — the pin test in
 * lineDrawingConstants.test.ts guards the two from drifting apart.
 */
export function correctedLengthFt(
  planFt: number,
  feature: LineType,
  pitch: string
): number {
  switch (feature) {
    case 'rake':
      return planFt * slopeFactor(pitch);
    case 'hip':
    case 'valley':
      return planFt * diagonalSlopeFactor(pitch);
    default:
      return planFt;
  }
}
